import { Controller } from "@hotwired/stimulus"
import * as bootstrap from "bootstrap"

export default class extends Controller {
  static targets = [
    "preset",
    "loadingOverlay",
    "toggleScenariosPanelButton",
    "smartScenariosPanel",
    "scenariosStartDate",
    "scenariosSpanMonths",
    "scenariosStatusBadge",
    "toggleScenariosStatusBadge",
    // batch change
    "startDate", "endDate", "periodType", "costCentre", "showChildCostCentres"
  ]

  static values = {
    fetchPath: String,
    adjustViewContextPath: String,
    viewContext: String,
    scenariosEnabled: Boolean,
    viewName: String,
    persistViewContextPath: String,
    contextPersistedByAdjust: Boolean
  }

  static eventNames = [
    "ledger:cash_flow_boundary_updated",
    "smart_schedule:created",
    "smart_schedule:updated",
    "smart_schedule:destroyed",
    "smart_scenario:created",
    "smart_scenario:updated",
    "smart_scenario:destroyed",
    "smart_scenario:reordered"
  ]

  connect() {
    // Table updates push their own URLs without a Turbo page visit. Turbo's
    // snapshot URL can therefore lag behind the rendered range.
    Turbo.cache.exemptPageFromCache()

    this.handlePopState = this.handlePopState.bind(this)
    window.addEventListener("popstate", this.handlePopState)

    this.isFetching = false
    this.pendingRequest = null

    this.initialViewContext = this.viewContextValue
    this.setViewContext(this.viewContextValue)

    this.initializeSmartScenariosPanel()

    this.eventHandler = this.loadTable.bind(this)

    this.constructor.eventNames.forEach(eventName => {
      document.addEventListener(eventName, this.eventHandler)
    })
  }

  disconnect() {
    // Clean up to avoid memory leaks
    window.removeEventListener("popstate", this.handlePopState)
    this.constructor.eventNames.forEach(eventName => {
      document.removeEventListener(eventName, this.eventHandler)
    })
  }

  initializeSmartScenariosPanel() {
    this.hasScenarios = this.hasSmartScenariosPanelTarget

    if (!this.hasScenarios) {
      return
    }

    this.bsSmartScenariosPanel = new bootstrap.Offcanvas(this.smartScenariosPanelTarget)

    // Sync button state when panel opens/closes
    this.smartScenariosPanelTarget.addEventListener('shown.bs.offcanvas', () => {
      this.toggleScenariosPanelButtonTarget.classList.add("active")
    })
    this.smartScenariosPanelTarget.addEventListener('hidden.bs.offcanvas', () => {
      this.toggleScenariosPanelButtonTarget.classList.remove("active")
    })

    this.updateScenariosStatusBadge()
  }

  toggleSmartScenariosPanel() {
    if (this.hasScenarios) {
      if (this.smartScenariosPanelTarget.classList.contains('show')) {
        this.bsSmartScenariosPanel.hide()
      } else {
        this.bsSmartScenariosPanel.show()
      }
    }
  }

  hideSmartScenariosPanel() {
    if (this.hasScenarios) {
      this.bsSmartScenariosPanel.hide()
    }
  }


  handlePopState(event) {
    // Turbo restores entries carrying its history state, including after reload.
    // A second table fetch would race that restoration and mutate its snapshots.
    // Entries pushed by this controller have no Turbo state and still need us.
    if (event.state?.turbo) return

    const urlParams = new URLSearchParams(window.location.search)
    const newViewContextParam = urlParams.get("view_context")

    if (newViewContextParam) {

      if (newViewContextParam !== this.viewContext) {
        this.setViewContext(newViewContextParam)
        this.loadTable(true)
      }

    } else {
      // No params → reset to initial values from data-values (server rendered)
      const initialContext = this.initialViewContext


      if (this.viewContext !== initialContext) {
        this.setViewContext(initialContext)
        this.loadTable(true)
      }
    }
  }

  shiftView(event) {
    const shiftMonths = parseInt(event.params.shift || 1, 10)

    this.adjustViewContextOnServer({ shiftMonths })
      .then(data => {
        this.setViewContext(data.view_context)
        this.loadTable()
        this.persistUserViewContext(this.viewContext)
      })
  }

  presetFromToday(event) {
    const spanMonths = parseInt(event.params.span || 12, 10)

    this.adjustViewContextOnServer({ spanMonthsFromNow: spanMonths })
      .then(data => {
        this.setViewContext(data.view_context)
        this.loadTable()
        this.persistUserViewContext(this.viewContext)
      })
  }

  batchAdjust(event) {
    const value = event.target.value

    let batch = {
      start_date: this.startDateTarget.value,
      end_date: this.endDateTarget.value,
      pt: this.periodTypeTarget.value,
      cost_centre: this.costCentreTarget.value,
      show_child_cost_centres: this.showChildCostCentresTarget.checked
    }

    this.adjustViewContextOnServer(batch)
      .then(data => {
        this.setViewContext(data.view_context)
        this.loadTable()
        this.persistUserViewContext(this.viewContext)
      })
  }

  selectVariance(event) {
    const selectedVariance = event.currentTarget.dataset.calendarTableVarianceParam

    this.updateVarianceButtonStyles(selectedVariance)
    this.toggleVarianceBadges(selectedVariance)

    this.adjustViewContextOnServer({ variance: selectedVariance })
      .then(data => {
        this.setViewContext(data.view_context)
        this.updateUrlHistory(this.viewContext)
        this.persistUserViewContext(this.viewContext)
      })
  }

  updateVarianceButtonStyles(selectedVariance) {
    document
      .querySelectorAll("button[data-calendar-table-variance-param]")
      .forEach(btn => {
        if (btn.dataset.calendarTableVarianceParam === selectedVariance) {
          btn.classList.remove("btn-outline-secondary")
          btn.classList.add("btn-secondary")
        } else {
          btn.classList.remove("btn-secondary")
          btn.classList.add("btn-outline-secondary")
        }
      }
      )
  }

  toggleVarianceBadges(selectedVariance) {
    document.querySelectorAll("[data-variance]").forEach(el => {
      if (el.dataset.variance === selectedVariance) {
        el.classList.remove("d-none")
      } else {
        el.classList.add("d-none")
      }
    })
  }

  persistUserViewContext(viewContext) {
    if (this.contextPersistedByAdjustValue) return

    fetch(this.persistViewContextPathValue, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content || ""
      },
      body: JSON.stringify({
        view_name: this.viewNameValue,
        view_context: viewContext
      })
    }).then(response => {
      if (!response.ok) console.error("Failed to save schedule view context")
    })
  }

  setViewContext(viewContext) {
    this.viewContext = viewContext
    this.viewContextValue = viewContext
  }

  adjustViewContextOnServer(params) {
    const url = new URL(`${this.adjustViewContextPathValue}.json`, window.location.origin)

    // Always include current view_context
    url.searchParams.set("view_context", this.viewContext)

    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => url.searchParams.append(`${key}[]`, v))
      } else {
        url.searchParams.set(key, value)
      }
    })

    return fetch(url.toString())
      .then(response => response.json())
  }

  loadTable(dontPushHistory) {
    const contextStr = this.viewContext

    const requestParams = { contextStr }

    // If a fetch is already in flight, save the latest request and return
    if (this.isFetching) {
      this.pendingRequest = requestParams
      return
    }

    this.performFetch(dontPushHistory, requestParams)
  }

  performFetch(dontPushHistory, { contextStr }) {
    this.showLoading()

    this.isFetching = true

    if (!dontPushHistory) {
      this.updateUrlHistory(contextStr)
    }

    const url = new URL(`${this.fetchPathValue}.turbo_stream`, window.location.origin)
    url.searchParams.set("view_context", contextStr)

    fetch(url.toString(), {
      headers: {
        Accept: "text/vnd.turbo-stream.html"
      }
    })
      .then(response => response.text())
      .then(html => Turbo.renderStreamMessage(html))
      .finally(() => {
        this.isFetching = false
        this.finishedLoading()

        // If a request was queued while we were fetching, process it now
        if (this.pendingRequest) {
          const nextRequest = this.pendingRequest
          this.pendingRequest = null
          this.performFetch(false, nextRequest)
        }
      })
  }

  updateUrlHistory(contextStr) {
    const url = new URL(window.location)
    url.searchParams.set("view_context", contextStr)
    history.pushState({}, "", url.toString())
  }

  showLoading() {
    this.loadingOverlayTarget.classList.remove("d-none")
  }

  hideLoading() {
    this.loadingOverlayTarget.classList.add("d-none")
  }

  finishedLoading() {
    this.hideLoading()
  }


  enableScenarios() {
    this.scenariosEnabledValue = true
    this.applyScenarios()
  }

  disableScenarios() {
    this.scenariosEnabledValue = false
    this.applyScenarios()
  }

  applyScenarios() {
    const ssEnabled = this.scenariosEnabledValue
    const ssStartDate = this.scenariosStartDateTarget.value
    const ssSpanMonths = parseInt(this.scenariosSpanMonthsTarget.value, 10)
    const ssScenarios = this.collectSelectedScenarioIds()

    this.adjustViewContextOnServer({
      ss: true,
      ss_enabled: ssEnabled,
      ss_start_date: ssStartDate,
      ss_span_months: ssSpanMonths,
      ss_scenarios: ssScenarios
    })
      .then(data => {
        this.setViewContext(data.view_context)
        this.loadTable()
        this.updateScenariosStatusBadge()
        this.persistUserViewContext(this.viewContext)
      })
  }

  collectSelectedScenarioIds() {
    return Array.from(
      this.smartScenariosPanelTarget.querySelectorAll("input.smart-scenario-selection[type=checkbox]:checked")
    ).map(cb => parseInt(cb.value, 10))
  }

  updateScenariosStatusBadge() {
    this.scenariosStatusBadgeTargets.forEach(badge => {
      if (this.scenariosEnabledValue) {
        badge.textContent = "On"
        badge.classList.remove("bg-secondary")
        badge.classList.add("bg-success")
      } else {
        badge.textContent = "Off"
        badge.classList.remove("bg-success")
        badge.classList.add("bg-secondary")
      }
    })

    this.toggleScenariosStatusBadgeTargets.forEach(badge => {
      badge.textContent = "On"
      badge.classList.toggle("d-none", !this.scenariosEnabledValue)
    })
  }

  // onCashFlowBoundaryUpdated() {
  //   this.loadTable()
  // }
}
